<template>
  <div class="flex-1 pt-40">
    <t-loading :text="t('pages.login.login_loading')" size="small" :loading="pageLoading">
      <LoginForm
        @login="handleLogin"
        @lark-login="handleLarkLogin"
        :loading="loginLoading"
        v-if="!showError"
      />
      <LoginError v-else @change-login="showError = false" :error-type="errorType" />
    </t-loading>
    <t-dialog
      v-model:visible="showDialog"
      :close-btn="true"
      :header="t('pages.login.choose_organization')"
      :cancel-btn="null"
      :confirmBtn="null"
      @close="getLoginInitData"
    >
      <template #body>
        <t-loading :loading="loginLoading" size="small">
          <div class="flex flex-col gap-2.5">
            <div
              class="organization-item"
              @click="handleChooseTenant(tenant)"
              v-for="tenant in tenantList"
              :key="tenant.tenantId"
            >
              <div class="flex items-center gap-2">
                <t-image :src="tenant.img" :style="{ height: '40px' }" />
                <div class="text-sm text-[rgba(0,0,0,0.90)]">{{ tenant.tenantName }}</div>
              </div>

              <t-icon
                name="arrow-right-up"
                style="color: rgba(0, 0, 0, 0.9); font-size: 1rem"
                class="hidden icon"
              />
            </div>
          </div>
        </t-loading>
      </template>
    </t-dialog>
  </div>
</template>
<script setup lang="ts">
import LoginApi from "@/api/modules/login";
import { XTenantID, CONGRESS, REDIRECTURI, CLIENT_ID } from "@/config/web-config";
import Cookies from "js-cookie";
import LoginForm from "./@components/LoginForm.vue";
import LoginError from "./@components/LoginError.vue";
import { MessagePlugin } from "tdesign-vue-next";
import router from "@/router";
type TenantItem = {
  tenantId: string;
  tenantName: string;
  img: string;
};
const { t, locale } = useI18n();
definePage({
  meta: {
    layout: "login-layout",
  },
});
const loginLoading = ref(false);
const showError = ref(false);
const errorType = ref<1 | 2 | 3>(1);
const state = ref("");
const larkLink = ref("");
const token = ref("");
const route = useRoute();
const pageLoading = ref(false);
const showDialog = ref(false);
const tenantList = ref<TenantItem[]>([]);
const retryCount = ref(0);
const fingerprint=ref('')
const solarbrainPathMap: any = {
  "1163545568971063296": {
    tenant: 1,
    path: import.meta.env.VITE_AU_SOLARBRAIN_SITE,
  },
  "1168886458304430080": {
    tenant: 5,
    path: import.meta.env.VITE_NL_SOLARBRAIN_SITE,
  },
  "1168887101500948480": {
    tenant: 6,
    path: import.meta.env.VITE_PL_SOLARBRAIN_SITE,
  },
  "1168887272783740928": {
    tenant: 7,
    path: import.meta.env.VITE_US_SOLARBRAIN_SITE,
  },
};
const handleLarkLogin = () => {
  window.location.href = larkLink.value;
};
type LoginFormData = {
  username?: string;
  password?: string;
  authType?: string;
  remeBerMe?: boolean;
};
const loginFormData = ref<LoginFormData>({});
const removeQueryLocal = () => {
  localStorage.removeItem(REDIRECTURI);
  localStorage.removeItem(CLIENT_ID);
};
const handleLogin = async (formData: LoginFormData) => {
  loginLoading.value = true;
  loginFormData.value = formData;
  LoginApi.doLogin({ ...formData, state: state.value })
    .then(async () => {
      getTenantList();
    })
    .catch(async(e) => {
      // 登录无指纹错误，清空本地指纹
      if(e.code === 10004){
        Cookies.remove('fingerprint')
      }
      // 登录无指纹或者登录state过期重试机制
     if(e.code === 10003 || e.code === 10004){
        retryCount.value++;
        if (retryCount.value < 2) {
          await getLoginInitData();
          handleLogin(loginFormData.value);
        }else{
          retryCount.value=0
          MessagePlugin.error(e.message)
          loginLoading.value = false;

        }
      }else if(e.code === 10001){
         // 多次输入密码错误
        loginError(1);
        loginLoading.value = false;
      }else if(e.code === 10002){
        // 长时间未登录
        loginError(2);
        loginLoading.value = false;
      }else{
        MessagePlugin.error(e.message)
        loginLoading.value = false;
      }
    });
};
const getTenantList = () => {
  loginLoading.value = true;
  LoginApi.getTenantList()
    .then(async (res) => {
      if (!Array.isArray(res) || !res.length) {
        loginError(3);
        loginLoading.value = false;
        return;
      }
      tenantList.value = res as TenantItem[];
      if (res.length < 2) {
        Cookies.set(XTenantID, res[0].tenantId);
        loginCallBack(res[0].tenantId);
        return;
      }
      loginLoading.value = false;
      showDialog.value = true;
    })
    .catch(() => {
      loginLoading.value = false;
    });
};
const handleChooseTenant = (tenant: TenantItem) => {
  Cookies.set(XTenantID, tenant.tenantId);
  loginCallBack(tenant.tenantId);
};
const loginCallBack = async (tenantId: string) => {
  loginLoading.value = true;
  try {
    await confirmTenant(tenantId);
    MessagePlugin.success(t("pages.login.sign_in_sign_success"));
    const redirect_uri = localStorage.getItem(REDIRECTURI);
    const client_id = localStorage.getItem(CLIENT_ID);
    // 兼容老solarbrain，例如：从au过来但是选择租户是pl，这种情况不跳回au而是进入新选择的租户地址
    if (
      client_id &&
       solarbrainPathMap[client_id]
      && `${tenantId}` !==`${solarbrainPathMap?.[client_id]?.tenant}`
    ) {
      const currentSolarbrain:any=Object.values(solarbrainPathMap).find((item:any)=>{
        return `${item.tenant}`===`${tenantId}`
      })
      const path=currentSolarbrain?.path?currentSolarbrain?.path:redirect_uri?redirect_uri:import.meta.env.VITE_APP_PORTAL_WEBSITE
      toApplication(path,true)
      removeQueryLocal();
      return;
    }
    if (redirect_uri) {
      toApplication(redirect_uri,true);
      removeQueryLocal();
      return;
    }
    getApp();
  } catch (e) {
    loginLoading.value = false;
    MessagePlugin.error(t("pages.login.sign_in_sign_failed"));
  }
};
const confirmTenant = (tenantId: string) => {
  return new Promise((resolve, reject) => {
    LoginApi.confirmTenant(tenantId)
      .then((res:any) => {
        Cookies.set(CONGRESS, res);
        token.value=res
        resolve(true);
      })
      .catch((e) => {
        reject(false);
      });
  });
};
const getApp = () => {
  loginLoading.value = true;
  LoginApi.getAppList()
    .then((res) => {
      if (!Array.isArray(res) || !res.length) {
        loginError(3);
        loginLoading.value = false;
      } else if (res.length < 2) {
        const url = res[0].loginUrl || import.meta.env.VITE_APP_PORTAL_WEBSITE;
        toApplication(url,true);
      } else {
        toApplication(import.meta.env.VITE_APP_PORTAL_WEBSITE,true);
      }
    })
    .catch((e) => {
      loginError(3);
      loginLoading.value = false;
    });
};
const loginError = (type: 1 | 2 | 3) => {
  errorType.value = type;
  showError.value = true;
};
const getLoginInitData = () => {
  loginLoading.value = true;
  return new Promise((resolve) => {
    fingerprint.value=Cookies.get('fingerprint') || ''
    LoginApi.getLoginInit({ remember_me: true,only:fingerprint.value?'0':'1' })
      .then((res: any) => {
        if (!res) return;
        state.value = res.state;
        larkLink.value = res.lark;
        if (!fingerprint.value) {
          fingerprint.value = res.only;
          Cookies.set("fingerprint", fingerprint.value as string);
        }
      })
      .finally(() => {
        loginLoading.value = false;
        resolve(true);
      });
  });
};
const loginVerification = (appCode: string) => {
  return new Promise((resolve) => {
    LoginApi.loginVerification(appCode)
      .then((res: any) => {
        if (!res) {
          resolve(false);
          return;
        }
        token.value=res.token
        resolve(true);
      })
      .catch(() => {
        resolve(false);
      });
  });
};
const toApplication=(originalUrl:string,isLogin?:boolean)=>{
  originalUrl=removeFingerprintFromRedirectUri(originalUrl)

  try {
    const targetUrl = new URL(originalUrl, window.location.origin);
    const excludedQueryKeys = new Set(['redirect_uri', 'fingerprint', 'token', 'client_id', 'is_login', 'lang']);

    Object.entries(route.query).forEach(([key, value]) => {
      if (excludedQueryKeys.has(key)) {
        return;
      }

      const values = Array.isArray(value) ? value : [value];
      values.forEach((item) => {
        if (item !== undefined && item !== null) {
          targetUrl.searchParams.append(key, String(item));
        }
      });
    });

    targetUrl.searchParams.set('is_login', isLogin ? '1' : '0');
    targetUrl.searchParams.set('fingerprint', fingerprint.value || '');
    targetUrl.searchParams.set('token', token.value || '');
    targetUrl.searchParams.set('lang', locale.value);
    window.location.replace(targetUrl.toString());
  } catch (error) {
    console.error('Failed to build application redirect URL', error);
  }
}
function removeFingerprintFromRedirectUri(url:string) {
  try {
    const redirectUriObj = new URL(url);
    redirectUriObj.searchParams.delete("fingerprint");
    return redirectUriObj.toString();
  } catch (error) {
    console.error(error);
    return url;
  }
}
onMounted(async () => {
fingerprint.value=Cookies.get('fingerprint') || ''
  const redirect_uri = route.query.redirect_uri as string;
  const client_id = route.query.client_id as string;
  if (redirect_uri) {
    localStorage.setItem(REDIRECTURI, redirect_uri);
  }
  if (client_id) {
    localStorage.setItem(CLIENT_ID, client_id);
  }
  // 存在client_id表示从来solarbrain过来
  if (redirect_uri && client_id) {
    const isLogin = await loginVerification(client_id);
    if (isLogin) {
      removeQueryLocal();
      toApplication(redirect_uri);
      return;
    }
  }
  if (!redirect_uri && !route.query.code) {
    removeQueryLocal();
  }
  await getLoginInitData();
  if (route.query.code) {
   doLarkLogin()
  }
});
const doLarkLogin=()=>{
   pageLoading.value = true;
    LoginApi.larkLogin({ code: route.query.code as string })
      .then(() => {
        getTenantList();
      })
      .catch(async(e) => {
      // 登录无指纹错误，清空本地指纹
      if(e.code === 10004){
        Cookies.remove('fingerprint')
        await getLoginInitData();
        doLarkLogin()
        return
      }
      if(e.code === 10001){
         // 多次输入密码错误
        loginError(1);
      }else if(e.code === 10002){
        // 长时间未登录
        loginError(2);
      }else{
        MessagePlugin.error(e.message)
      }
       router.replace("/login");
      })
      .finally(() => {
        pageLoading.value = false;
      });
}
</script>
<style scoped>
.organization-item {
  @apply p-6 rounded-3 border-#E7E7E7 border-1px flex items-center justify-between cursor-pointer border-solid;
}

.organization-item:hover {
  @apply border-#06F bg-#F2F3FF;

  .icon {
    @apply block;
  }
}
</style>
